'use client'

import Link from 'next/link'
import { ReactNode, Suspense } from 'react'
import { AppNavigation } from '~/components/AppNav'
import { AppContainer } from '~/components/AppContainer'
import { IconTaloflow } from '~/components/Icons/IconTaloflow'
import { TalkToExpertLink } from '~/components/TalkToExpertLink'
import { WorkspaceSwitch } from './_components/WorkspaceSwitch'
import { TaloErrorBoundary } from '~/components/TaloErrorBoundary'
import { usePathname } from 'next/navigation'
import { InlineTooltip } from '~/components/Tooltip'
import {
  DashboardEvaluationsContextProvider,
  useDashboardWorkspaceContext,
} from './dashboardContext'
import { cn } from '~/utils/cn'
import { NewWorkspaceModal } from './workspace/modals/NewWorkspaceModal/NewWorkspaceModal'
import { EditWorkspaceModal } from './workspace/modals/EditWorkspaceModal/EditWorkspaceModal'
import { isWorkspaceMemberViewer } from './workspace/_utils'
import { useTranslation } from '~/i18n/useTranslation'

type DashboardLayoutProps = {
  children: ReactNode
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const pathname = usePathname()
  const { member, status } = useDashboardWorkspaceContext()
  const { t } = useTranslation()

  return (
    <DashboardEvaluationsContextProvider>
      <AppNavigation
        left={
          <div className="flex gap-2 items-center">
            {['/account/password'].includes(pathname) ? (
              <InlineTooltip tooltip={t('dashboard.backToAccount')}>
                <Link href="/account">
                  <IconTaloflow
                    className="w-12 h-12 text-primary-500"
                    revertOnHover
                  />
                </Link>
              </InlineTooltip>
            ) : ['/workspace', '/account'].includes(pathname) ? (
              <InlineTooltip tooltip={t('dashboard.backToDashboard')}>
                <Link href="/">
                  <IconTaloflow
                    className="w-12 h-12 text-primary-500"
                    revertOnHover
                  />
                </Link>
              </InlineTooltip>
            ) : (
              <IconTaloflow className="w-12 h-12 text-primary-500" />
            )}
            <TaloErrorBoundary>
              <WorkspaceSwitch />
            </TaloErrorBoundary>
          </div>
        }
      >
        <div className="flex-1 space-x-2 h-8 flex flex-row justify-end">
          {/* Only show Create evaluation button when context is loaded and member is available */}
          {status !== 'loading' && !isWorkspaceMemberViewer(member) ? (
            <Link
              href="/evaluations/new"
              className="bg-primary-500 ring-1 ring-primary-500 text-sm rounded px-3 py-1.5 text-white flex gap-x-2 items-center"
            >
              {t('dashboard.createEvaluation')}
            </Link>
          ) : null}

          <TalkToExpertLink intent="primary" />
        </div>
      </AppNavigation>
      <AppContainer
        className={cn('py-6 px-4', {
          'bg-[#F4F6F7] min-h-[calc(100vh-3rem)] ': [
            '/account',
            '/workspace',
          ].includes(pathname),
        })}
      >
        <Suspense>
          {children}
        </Suspense>
        <NewWorkspaceModal />
        <EditWorkspaceModal />
      </AppContainer>
    </DashboardEvaluationsContextProvider>
  )
}
